import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Drawer, Text, createSlotRecipeContext} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import DisplayPrice from '../../../../../leap/components/display-price'
import InputField from '../../../../../leap/components/input-field'
import {ENGRAVING_TEXT_PATTERN, MAX_ENGRAVING_CHARACTERS} from '../../../../../constants'
import {overlayRecipe} from './recipe'
import {useProductPreliminaryPrice} from '../../../../../hooks/use-product'
import ProductDisplayPrice from '../../../../../components/product-display-price'
import useProductPricing from '../../../../../hooks/use-product-pricing'
import EngravingStackableImage from '../../../partials/engraving-stackable-image'

const {StylesProvider, useRecipeResult, useStyles} = createSlotRecipeContext({
    recipe: overlayRecipe
})
const EMPTY_RECIPE_PROPS = {}

const OverlayFormFields = ({control, errors, trigger}) => {
    const {formatMessage} = useIntl()
    const styles = useStyles()

    const textRules = {
        validate: (value) => {
            if (!value || value.length === 0) {
                return formatMessage({
                    id: 'product_detail.engraving_modal.required',
                    defaultMessage: 'Required'
                })
            }

            if (value.length > MAX_ENGRAVING_CHARACTERS) {
                return formatMessage({
                    id: 'product_detail.engraving_modal.input_placeholder',
                    defaultMessage: 'Max 14 characters'
                })
            }

            if (!ENGRAVING_TEXT_PATTERN.test(value)) {
                return formatMessage({
                    id: 'product_detail.engraving_modal.invalid_characters',
                    defaultMessage: 'Only letters and spaces are allowed.'
                })
            }

            return true
        }
    }

    return (
        <Box css={styles.fields}>
            <InputField
                id="engraving-text"
                label={
                    <Text css={styles.nameLabel}>
                        <FormattedMessage
                            id="product_detail.engraving_modal.input_label"
                            defaultMessage="Your text"
                        />
                    </Text>
                }
                name="text"
                control={control}
                rules={textRules}
                invalid={Boolean(errors.text)}
                errorText={errors.text?.message}
                placeholder={formatMessage({
                    id: 'product_detail.engraving_modal.input_placeholder',
                    defaultMessage: 'Max 14 characters'
                })}
                endElement={({value}) => (
                    <Text id="engraving-char-count" css={styles.counter} aria-live="polite">
                        {value.length}
                        {' / '}
                        {MAX_ENGRAVING_CHARACTERS}
                    </Text>
                )}
                onChange={() => {
                    trigger('text')
                }}
            />
            <Box>
                <InputField
                    type="checkbox"
                    name="termsAccepted"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value === true ||
                            formatMessage({
                                id: 'configurator.step.engraving.error.termsRequired',
                                defaultMessage: 'Please agree to the Terms & Conditions'
                            })
                    }}
                    invalid={Boolean(errors.termsAccepted)}
                    label={
                        <Text textStyle="body.small">
                            {formatMessage({
                                id: 'product_detail.engraving_modal.i_understand',
                                defaultMessage: 'Ok, I understand Terms & Conditions.'
                            })}
                        </Text>
                    }
                    helperText={
                        <FormattedMessage
                            id="product_detail.engraving_modal.terms"
                            defaultMessage="Your personalised Tripp Trapp is very unique and made especially for you. Therefore, we need two extra business days to process your order. Please make sure that your requested engraving is written correctly since we are not accountable for any misspellings. Please note that once a Tripp TrappⓇ has been engraved, it cannot be returned or refunded."
                        />
                    }
                    errorText={errors.termsAccepted?.message}
                />
            </Box>
        </Box>
    )
}

OverlayFormFields.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    trigger: PropTypes.func.isRequired
}

const OverlayFooter = ({product}) => {
    const styles = useStyles()
    const {data: productPrice, isLoading: isPriceLoading} = useProductPreliminaryPrice(product)
    const {isFree} = useProductPricing(productPrice) || {}

    return (
        <Drawer.Footer css={styles.footer}>
            {!isFree && (
                <ProductDisplayPrice
                    as={DisplayPrice}
                    product={productPrice}
                    loading={isPriceLoading}
                />
            )}
            <Button size="lg" width="auto" variant="primaryFilled" type="submit">
                <Text>
                    <FormattedMessage id="global.update" defaultMessage="Update" />
                </Text>
            </Button>
        </Drawer.Footer>
    )
}

OverlayFooter.propTypes = {
    product: PropTypes.object
}

const Overlay = ({alt, stackImages, product, onSubmit}) => {
    const {styles} = useRecipeResult(EMPTY_RECIPE_PROPS)

    const {
        control,
        handleSubmit,
        watch,
        setFocus,
        formState: {errors},
        trigger
    } = useForm({
        defaultValues: {
            text: product.variationValues.text,
            termsAccepted: false
        }
    })

    const text = watch('text') ?? ''
    const onFormSubmit = ({text}) => onSubmit({text})
    const onFormError = (formErrors) => {
        if (formErrors.text) {
            setFocus('text')
            return
        }

        if (formErrors.termsAccepted) {
            setFocus('termsAccepted')
            return
        }
    }

    return (
        <StylesProvider value={styles}>
            <Box as="form" onSubmit={handleSubmit(onFormSubmit, onFormError)} css={styles.form}>
                <Drawer.Body css={styles.body}>
                    <Box width="100%" maxW={{base: '375px', xs: '448px'}} mx="auto">
                        <EngravingStackableImage
                            alt={alt}
                            images={stackImages}
                            product={product}
                            engraveText={text}
                        />
                    </Box>
                    <OverlayFormFields control={control} errors={errors} trigger={trigger} />
                </Drawer.Body>
                <OverlayFooter product={product} />
            </Box>
        </StylesProvider>
    )
}

Overlay.propTypes = {
    alt: PropTypes.string,
    product: PropTypes.object,
    stackImages: PropTypes.array,
    onSubmit: PropTypes.func.isRequired
}

export default Overlay
